import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import DOMPurify from 'dompurify';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { ToolTipProps } from './types';
import { TooltipArrow, TooltipContainer, TooltipContent, TooltipMessage } from './styles';

export enum ToolTipPosition {
  right = 'right',
  bottom = 'bottom',
  left = 'left',
  top = 'top',
  topLeft = 'top-left',
  topRight = 'top-right',
  bottomLeft = 'bottom-left',
  bottomRight = 'bottom-right',
}

const growDownPosition = [ToolTipPosition.bottomLeft, ToolTipPosition.bottomRight];
const growUpPosition = [ToolTipPosition.topLeft, ToolTipPosition.topRight];

const Tooltip: React.FC<ToolTipProps> = (props) => {
  const {
    centered,
    children,
    disabled,
    message,
    position = ToolTipPosition.bottom,
    isMobile,
  } = props;
  const { actualTheme, isMobile: mobileTheme } = useContext(ThemeContext);

  const useMobileVersion = useMemo(() => isMobile ?? mobileTheme, [isMobile, mobileTheme]);

  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const childRef = useRef<HTMLDivElement | null>(null);
  const messageRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  const getPosition = useCallback(() => {
    if (!childRef.current || !tipRef.current) return null;

    const { x, y, width, height } = childRef.current.getBoundingClientRect();
    const { width: tipWidth, height: tipHeight } = tipRef.current.getBoundingClientRect();
    const arrowTipHeight = 16;

    switch (position) {
      case ToolTipPosition.bottom:
        return {
          top: `${y + height + 12}px`,
          left: `${x + width / 2 - 2}px`,
        };
      case ToolTipPosition.top:
        return {
          top: `${y - tipHeight - 12}px`,
          left: `${x + width / 2 - 2}px`,
        };
      case ToolTipPosition.left:
        return {
          top: `${y + height / 2 - tipHeight / 2}px`,
          left: `${x - tipWidth - 12}px`,
        };
      case ToolTipPosition.right:
        return {
          top: `${y + height / 2 - tipHeight / 2}px`,
          left: `${x + width + 12}px`,
        };
      case ToolTipPosition.bottomRight:
        return {
          top: `${y - (arrowTipHeight - height / 2)}px`,
          left: `${x + width + 12}px`,
        };
      case ToolTipPosition.bottomLeft:
        return {
          top: `${y - (arrowTipHeight - height / 2)}px`,
          left: `${x - tipWidth - 12}px`,
        };
      case ToolTipPosition.topLeft:
        return {
          top: `${y - tipHeight + (arrowTipHeight + height / 2)}px`,
          left: `${x - tipWidth - 12}px`,
        };
      case ToolTipPosition.topRight:
        return {
          top: `${y - tipHeight + (arrowTipHeight + height / 2)}px`,
          left: `${x + width + 12}px`,
        };
      default:
        return {
          top: '0px',
          left: '0px',
        };
    }
  }, [position]);

  const handleMouseEnter = useCallback(() => {
    if (!tipRef.current || disabled) {
      return;
    }

    const pos = getPosition();

    if (pos) {
      tipRef.current.style.top = pos.top;
      tipRef.current.style.left = pos.left;
    }
  }, [disabled, getPosition]);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const pos = getPosition();

      if (pos) {
        tipRef.current.style.top = pos.top;
        tipRef.current.style.left = pos.left;
      }
      setIsVisible(true);
    }
  }, [getPosition, isOpen]);

  const stopPropagation = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation();

  const closeToolTip = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('click', closeToolTip);
    return () => document.removeEventListener('click', closeToolTip);
  }, [closeToolTip]);

  return (
    <ThemeProvider theme={actualTheme}>
      <TooltipContainer
        data-testid="tooltip-root"
        disabled={disabled}
        isMobile={useMobileVersion}
        isOpen={isOpen}
        isVisible={isVisible}
      >
        <div
          ref={childRef}
          role="presentation"
          onMouseEnter={handleMouseEnter}
          onClick={useMobileVersion ? handleClick : undefined}
        >
          {children}
        </div>
      </TooltipContainer>

      <TooltipContent
        data-testid="tooltip-label"
        position={position}
        ref={tipRef}
        onClick={useMobileVersion ? stopPropagation : undefined}
        shouldGrowCentered={
          !growDownPosition.includes(position) && !growUpPosition.includes(position)
        }
      >
        <TooltipArrow
          position={position}
          shouldGrowDown={growDownPosition.includes(position)}
          shouldGrowUp={growUpPosition.includes(position)}
        />
        <TooltipMessage
          centered={centered}
          ref={messageRef}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(message, { USE_PROFILES: { html: true } }),
          }}
        />
      </TooltipContent>
    </ThemeProvider>
  );
};

export default Tooltip;
